// Accept-invite page (ADR-179 P3 / section 7-E). UNAUTHENTICATED — reached
// by following the one-time invite URL/token an owner/admin delivered
// out-of-band (this build has no email infrastructure — see
// customer-api.ts's acceptInvite doc). Reads ?token= from the URL query
// directly (no router-level search-param schema exists in this app yet;
// every other route here is a plain path, so a lightweight
// window.location read keeps this route self-contained rather than
// introducing the first search-param convention for one page).
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto the shared genesis.ts tokens + glass-card idiom, matching the rest
// of the portal shell.
import { useState } from 'react'
import { friendlyError } from '../lib/auth/auth-http'
import { useNavigate } from '@tanstack/react-router'
import { acceptInvite } from '@/lib/auth/customer-api'
import { CHARCOAL, GREY_400, WHITE, errorBlockStyle, primaryButtonStyle } from '@/lib/genesis'

function tokenFromLocation(): string {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('token') ?? ''
}

export function PortalAcceptInvitePage() {
  const navigate = useNavigate()
  const [token] = useState(tokenFromLocation)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) {
      setError('This invite link is missing its token.')
      return
    }
    if (password.length < 12) {
      setError('Password must be at least 12 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      await acceptInvite(token, password)
      setDone(true)
    } catch (err) {
      setError(friendlyError(err, 'Failed to accept invite.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6" style={{ backgroundColor: CHARCOAL }}>
        <div className="glass-card max-w-sm p-6 text-center">
          <h1 className="font-display mb-2 text-lg font-semibold" style={{ color: WHITE }}>You're all set</h1>
          <p className="mb-4 text-sm" style={{ color: GREY_400 }}>
            Your account is ready. Sign in with your new password to continue.
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={primaryButtonStyle}
          >
            Go to sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6" style={{ backgroundColor: CHARCOAL }}>
      <form onSubmit={handleSubmit} className="glass-card w-full max-w-sm p-6">
        <h1 className="font-display mb-1 text-lg font-semibold" style={{ color: WHITE }}>Accept your invite</h1>
        <p className="mb-5 text-sm" style={{ color: GREY_400 }}>Set a password to finish joining your account.</p>

        {!token && (
          <div className="mb-4 p-3" style={errorBlockStyle}>
            <p className="text-sm" style={{ color: WHITE }}>
              No invite token found in this link. Ask whoever invited you for a fresh link.
            </p>
          </div>
        )}

        <label htmlFor="invite-password" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
          New password
        </label>
        <input
          id="invite-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="glass-input mb-3 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          autoComplete="new-password"
        />

        <label htmlFor="invite-confirm" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
          Confirm password
        </label>
        <input
          id="invite-confirm"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="glass-input mb-4 w-full px-3 py-2 text-sm"
          style={{ color: WHITE }}
          autoComplete="new-password"
        />

        {error && (
          <div className="mb-4 p-3" style={errorBlockStyle}>
            <p className="text-sm" style={{ color: WHITE }}>{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !token}
          className="w-full rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
          style={primaryButtonStyle}
        >
          {submitting ? 'Setting up…' : 'Accept invite'}
        </button>
      </form>
    </div>
  )
}
